<template>
  <div
    :class="{
      relative: !isMobile,
      'bg-white absolute z-2 inset-0 flex flex-col items-stretch justify-center mt-1 gap-1':
        mobileSearchMode && isMobile,
    }"
  >
    <template v-if="isMobile">
      <div
        v-show="mobileSearchMode"
        class="w-full flex items-center gap-3 py-1 px-4"
      >
        <a-input
          ref="mobSearchInput"
          v-model:value="searchText"
          placeholder="Search..."
          class="cust-input group h-10 flex-1 bg-[#f5f5f5]!"
          allowClear
        >
          <template #prefix>
            <div
              class="text-gray-400 group-focus-within:text-blue transition-colors"
            >
              <SearchIcon class="size-5 mr-1" />
            </div>
          </template>
          <template #clearIcon>
            <XIcon class="w-4 h-4 text-gray-500" />
          </template>
        </a-input>
        <button
          class="text-sm font-medium text-[#6E6E6E]"
          @click="closeSearchMode"
        >
          Cancel
        </button>
      </div>
    </template>
    <template v-else>
      <div class="my-1 w-[240px] lg:w-[400px] transition-all duration-300">
        <a-input
          v-model:value="searchText"
          placeholder="Search questions..."
          class="cust-input group h-10 hover:bg-gray-50!"
          allowClear
        >
          <template #prefix>
            <div
              class="text-gray-400 group-focus-within:text-blue transition-colors"
            >
              <SearchIcon class="size-5 mr-1" />
            </div>
          </template>
          <template #clearIcon>
            <XIcon class="w-4 h-4 text-gray-500" />
          </template>
        </a-input>
      </div>
    </template>
    <a-dropdown
      v-model:open="searchDropdownOpen"
      placement="bottomRight"
      :trigger="['click']"
      :getPopupContainer="(trigger) => trigger?.parentNode"
      :style="{ width: '100%' }"
    >
      <div class="w-full"></div>
      <template #overlay>
        <a-menu
          v-if="searchDropdownOpen"
          class="py-2! px-0! overflow-y-auto max-h-[80vh]"
        >
          <!-- Loading -->
          <div
            v-if="isSearching"
            class="px-4 py-6 flex flex-col items-center justify-center gap-1"
          >
            <a-spin size="small" />
            <span class="text-xs text-gray-400">Getting you sorted...</span>
          </div>

          <!-- Results -->
          <template v-else>
            <div v-if="searchResults.length" class="mb-1">
              <div
                class="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider"
              >
                Questions 🎯
              </div>
              <QuestionNavCard
                v-for="q in searchResults"
                :key="q.uuid"
                :title="q.content"
                :catLabel="q.topic"
                @click="handleNavLink(q.uuid)"
              />
            </div>
            <!-- No results -->
            <div v-else class="px-4 py-6 text-center text-sm text-gray-400">
              No questions found for "{{ searchText }}"
            </div>
          </template>

          <div
            v-if="!isSearching && searchResults.length"
            class="p-3 bg-gradient-to-r from-[#F8FBFF] to-[#EEF5FF] translate-y-2 border-t border-gray-200 flex items-center justify-center gap-2 sticky bottom-0"
          >
            <SparklesIcon class="w-3.5 h-3.5 text-blue" />
            <span class="text-xs font-medium text-gray-600">
              Found {{ searchResults.length }} result{{
                searchResults.length !== 1 ? 's' : ''
              }}
            </span>
          </div>
        </a-menu>
      </template>
    </a-dropdown>
  </div>
  <button
    v-if="!mobileSearchMode && isMobile"
    class="p-2 text-[#1B1B1B] hover:bg-gray-50 rounded-lg transition-colors"
    @click="openSearchMode"
  >
    <SearchIcon class="w-6 h-6" />
  </button>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { SearchIcon, SparklesIcon, XIcon } from 'lucide-vue-next';
import QuestionNavCard from './QuestionNavCard.vue';
import { get } from '@/api';

const props = defineProps({
  isMobile: { type: Boolean, required: true },
});
const router = useRouter();
const searchText = ref('');
const mobileSearchMode = ref(false);
const mobSearchInput = ref(null);
const searchDropdownOpen = ref(false);
const searchResults = ref([]);
const isSearching = ref(false);

const closeSearchDropdown = () => {
  searchDropdownOpen.value = false;
  searchResults.value = [];
};
const closeSearchMode = () => {
  closeSearchDropdown();
  mobileSearchMode.value = false;
  searchText.value = '';
};
const openSearchMode = () => {
  mobileSearchMode.value = true;
  nextTick(() => {
    mobSearchInput.value?.focus();
  });
};
const handleNavLink = (uuid) => {
  const uuids = searchResults.value.map((q) => q.uuid);
  const questionMeta = Object.fromEntries(
    searchResults.value.map((question) => [
      question.uuid,
      {
        subject:
          question.paper_subject_name ||
          question.subject_name ||
          question.subject?.name ||
          '',
        topics: (question.topics || [])
          .map((topic) => {
            if (typeof topic === 'string') return topic;
            return topic?.name;
          })
          .filter(Boolean),
        paper_number: question.paper_number ?? null,
        paper_year: question.paper_year ?? null,
        paper_season: question.paper_season ?? question.season ?? null,
        paper_variant: question.paper_variant ?? null,
      },
    ])
  );
  sessionStorage.setItem('topicWiseQuestionUuids', JSON.stringify(uuids));
  sessionStorage.setItem('topicWiseQuestionMeta', JSON.stringify(questionMeta));
  closeSearchDropdown();
  closeSearchMode();
  router.push({
    name: 'question-detail',
    params: { viewCategory: 'topic-wise', questionUuid: uuid },
  });
};

const runSearch = useDebounceFn(async (query) => {
  if (!query || query.length < 2) return;
  isSearching.value = true;
  try {
    const { data } = await get('students/api/search/', { q: query });
    searchResults.value = data?.questions ?? [];
  } catch {
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
}, 350);

watch(
  () => props.isMobile,
  () => {
    mobileSearchMode.value = false;
  }
);
watch(
  () => searchText.value,
  (v) => {
    if (!v) {
      closeSearchDropdown();
      return;
    }
    if (v.length >= 2) {
      searchDropdownOpen.value = true;
      runSearch(v);
    }
  }
);
</script>

<style scoped></style>
